import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import BackButton from "../../../../components/BackButton";
import SpinnerLoader from "../../../../components/SpinnerLoader";
import {
  downloadDescriptionFile,
  getLearnerCourseSessions,
  updateLearnerApplicationStatus,
} from "../../../../redux/slices/learner.applications.slice";
import { RootState, useAppDispatch } from "../../../../redux/store";
import SaveConfirmModal from "../../../../components/modal/SaveConfirmModal";
import EventDetailsModal from "../../../Sessions/components/EventDetailsModal";
import { useTranslation } from "react-i18next";
import LanguageSelector from "../../../../components/LanguageSelector";
import TabNavbar from "../../../../components/Tabbar";
import { LEARNER_TABS } from "../../../../utils/TabbarData";
import { showToast } from "../../../../utils/showToast";
import { extractCareerLevelNumber } from "../../../../utils/helper";

function CourseSessions() {
  const { pathname } = useLocation();
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { availableCourseSessions, loading, selectedCourse, updateLoading } =
    useSelector((state: RootState) => state.learnerApplications) as any;

  const [modalData, setModalData] = useState<any[]>([]); // Modal data for events
  const [isModalOpen, setIsModalOpen] = useState(false); // Modal state
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [currentSessionName, setCurrentSessionName] = useState("");

  const handleApply = async () => {
    const payload = {
      userId: sessionStorage.getItem("userId") || "",
      courseId: selectedCourse?.course_id,
      status: "APPLIED",
    };

    await dispatch(updateLearnerApplicationStatus(payload));

    setIsSaveModalOpen(false);
    navigate("/learner/applied-trainings");
  };

  useEffect(() => {
    const id = pathname.split("/")[3];

    if (id) {
      dispatch(
        getLearnerCourseSessions({
          course_id: id,
          role: "learner",
          user_id: sessionStorage.getItem("userId"),
        })
      );
    }
  }, [pathname, dispatch]);

  // Parse and set modal data for events
  const handleOpenModal = (events: string) => {
    // Step 1: Split by "," only outside of "({ })"
    // const eventBlocks = events.split(/(?<=\)}),/); // Match ", " only outside the "({ })"
    const eventBlocks = events.split(/;\s*/);
    const parsedEvents = eventBlocks.map((block) => {
      // Step 2: Split by "({"
      const [eventName, detailsBlock] = block.split("{(");

      // Step 3: Remove closing ")}" and split details inside "({ })" by ","
      const details = detailsBlock
        ?.replace(")}", "")
        ?.split(",")
        .map((item) => item.trim());

      // Step 4: Map details to fields
      const [date, time, mode, location, classroom, ...instructorsBlock] =
        details || [];

      // Step 5: Clean instructors
      // Step 5: Extract and clean instructors
      const instructors = instructorsBlock
        .join(", ") // Join remaining parts to recreate full instructors string
        .replace("Instructors:", "") // Remove the "Instructors:" prefix
        .trim();

      return {
        eventName: eventName?.trim(),
        date: date || null,
        time: time
          ? time
              .split("-")
              .map((t) => t.trim().slice(0, 5))
              .join("-")
          : null,
        mode: mode || null,
        location: location || null,
        classroom: classroom || null,
        instructors: instructors || null,
      };
    });

    setModalData(parsedEvents);
    setIsModalOpen(true);
  };
  const handleCloseModal = () => setIsModalOpen(false);

  const handleDownloadDescriptionFile = async () => {
    setIsDownloading(true);
    try {
      const response = await dispatch(
        downloadDescriptionFile({ course_id: selectedCourse?.course_id })
      );
      if (response.payload && response.payload.demo_file) {
        const fileUrl = response.payload.demo_file.startsWith('//') 
          ? `https:${response.payload.demo_file}` 
          : response.payload.demo_file;
        window.open(fileUrl, "_blank");
      } else {
        showToast(t("No download material for this course"), "info");
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const isMobileOrTablet = window.innerWidth < 1024; // Checking for mobile/tablet screens

  return (
    <div className="flex flex-col items-start justify-start gap-10">
      {isMobileOrTablet ? (
        <>
          {" "}
          <TabNavbar
            id=""
            tabs={LEARNER_TABS}
            selectedTabId={1}
            type="Available Trainings"
          />
          <BackButton
            to="/learner/available-trainings"
            state={{ from: "LearnerCourseSessions" }}
          />
        </>
      ) : (
        <>
          <BackButton
            to="/learner/available-trainings"
            state={{ from: "LearnerCourseSessions" }}
          />
        </>
      )}
      <div className="flex flex-row w-full justify-between items-center group cursor-pointer">
        <div className="flex flex-col w-full">
          <p className="text-2xl font-semibold text-[#961e38]">
            {selectedCourse?.course_name}
          </p>
          <div className="flex flex-wrap items-center justify-start gap-5 pt-2">
            {selectedCourse?.category_type && (
              <p className="text-xs font-semibold flex items-center justify-start gap-2">
                <span className="text-[#b5b5b5]">{t("Type")}:</span>
                <span>{selectedCourse?.category_type}</span>
              </p>
            )}

            <p className="text-xs font-semibold flex items-center justify-start gap-2">
              <span className="text-[#b5b5b5]">{t("Lang")}:</span>
              <span>
                {selectedCourse?.training_languages_oc
                  ? selectedCourse?.training_languages_oc
                  : t("N/A")}
              </span>
            </p>

            {selectedCourse?.minimum_job_level && (
              <p className="text-xs font-semibold flex items-center justify-start gap-2">
                <span className="text-[#b5b5b5]">{t("Min Career Lvl")}:</span>
                <span>
                  {extractCareerLevelNumber(selectedCourse?.minimum_job_level)}
                </span>
              </p>
            )}
            {selectedCourse?.creation_date && (
              <p className="text-xs font-semibold flex items-center justify-start gap-2">
                <span className="text-[#b5b5b5]">{t("Creation Date")}:</span>
                <span>{selectedCourse?.creation_date}</span>
              </p>
            )}
            {selectedCourse?.classroom_training_cost && (
              <p className="text-xs font-semibold flex items-center justify-start gap-2">
                <span className="text-[#b5b5b5]">
                  {t("Classroom Training Cost")}:
                </span>
                <span>{selectedCourse?.classroom_training_cost}</span>
              </p>
            )}
            {selectedCourse?.online_training_cost && (
              <p className="text-xs font-semibold flex items-center justify-start gap-2">
                <span className="text-[#b5b5b5]">
                  {t("Online Training Cost")}:
                </span>
                <span>{selectedCourse?.online_training_cost}</span>
              </p>
            )}
          </div>
        </div>
        {!isMobileOrTablet && <LanguageSelector />}
      </div>
      <div className="flex items-center w-full gap-5">
        <button
          className="py-1.5 px-5 bg-white uppercase disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ color: "#961e38", border: "1px solid #961e38" }}
          onClick={handleDownloadDescriptionFile}
          disabled={isDownloading}
        >
          {isDownloading ? t("Downloading...") : t("Download Description")}
        </button>
        <button
          className="py-1.5 px-5 text-white bg-[#961e38] uppercase disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={updateLoading || loading} //availableCourseSessions.length === 0
          onClick={() => setIsSaveModalOpen(true)}
        >
          {t("Apply and find me a seat")}
        </button>
      </div>

      {/* Mobile/Tablet View */}
      <div className="block md:hidden w-full">
        {loading ? (
          <div className="flex items-center w-full justify-center mt-4 mb-4">
            <SpinnerLoader />
          </div>
        ) : availableCourseSessions.length > 0 ? (
          availableCourseSessions.map((item: any, i: any) => {
            return (
              <div
                key={i}
                className="border border-[#c6c6c6] py-2 text-left bg-[#f3f3f3] mt-2"
              >
                <div className="px-4 border-b border-[#c6c6c6] pb-2">
                  <h3 className="font-[600] pb-2">{t("Session Name")}</h3>
                  <p className="text-lg"> {item?.session_name}</p>
                </div>
                <div className="px-4 border-b border-[#c6c6c6] pb-2">
                  <h3 className="font-[600] pb-2"> {t("Language")}</h3>
                  <p className="text-lg"> {item?.session_language}</p>
                </div>
                <div className="px-4 border-b border-[#c6c6c6] pb-2">
                  <h3 className="font-[600] pb-2"> {t("Location")}</h3>
                  <p className="text-lg">
                    {item?.type === "ONLINE"
                      ? t("N/A")
                      : item?.location &&
                        item?.location !== "UNDEFINED" &&
                        item?.type !== "NOT_SET"
                      ? item?.location
                      : "MULTIPLE LOCATIONS"}
                  </p>
                </div>
                <div className="px-4 border-b border-[#c6c6c6] pb-2">
                  <h3 className="font-[600] pb-2"> {t("Type")}</h3>
                  <p className="text-lg">
                    {item?.type === "ONSITE" ? t("Physical attendance") : t("Online")}
                  </p>
                </div>
                <div className="px-4 border-b border-[#c6c6c6] pb-2">
                  <h3 className="font-[600] pb-2"> {t("Session Details")}</h3>
                  <p className="text-lg">
                    {item.events ? (
                      <span
                        className="text-blue-500 cursor-pointer underline"
                        onClick={() => {
                          setCurrentSessionName(item.session_name);
                          handleOpenModal(item.events);
                        }}
                      >
                        {t("Open")}
                      </span>
                    ) : (
                      t("N/A")
                    )}
                  </p>
                </div>
                <div className="flex justify-between items-center px-4 pt-2">
                  <div>
                    <p className="text-lg">
                      <h3 className="font-[600]"> {t("Instructors")}</h3>
                      {item.instructors
                        ? item.instructors
                            .split(",")
                            .map(
                              (instructor: any, index: number, array: any) => (
                                <span key={index}>
                                  {instructor.trim()}
                                  {index !== array.length - 1 && ","} <br />
                                </span>
                              )
                            )
                        : t("N/A")}
                    </p>
                  </div>{" "}
                </div>
              </div>
            );
          })
        ) : (
          <p className="w-full text-center py-5">
            {" "}
            {t("No Session Data found for") + " "} {selectedCourse.course_name}
          </p>
        )}
        <EventDetailsModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          modalData={modalData}
          modalName="Session Details"
        />
      </div>

      {/* Desktop View */}
      <div className="hidden md:block w-full h-full">
        <table className="table-auto w-full">
          <thead>
            <tr>
              <th className="border-b border-[#961e38] py-2 px-4 w-1/5 text-center">
                {t("Session Name")}
              </th>
              <th className="border-b border-[#961e38] py-2 px-4 w-1/5 text-center">
                {t("Language")}
              </th>
              <th className="border-b border-[#961e38] py-2 px-4 w-1/5 text-center">
                {t("Location")}
              </th>
              <th className="border-b border-[#961e38] py-2 px-4 w-1/5 text-center">
                {t("Type")}
              </th>
              <th className="border-b border-[#961e38] py-2 px-4 w-1/5 text-center">
                {t("Session Details")}
              </th>
              <th className="border-b border-[#961e38] py-2 px-4 w-1/5 text-center">
                {t("Instructors")}
              </th>
              {/* <th className="border-b border-[#961e38] py-2 px-4 w-1/5 text-center">
                Session Status
              </th> */}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="text-center">
                  <div className="flex items-center w-full justify-center mt-4 mb-4">
                    <SpinnerLoader />
                  </div>
                </td>
              </tr>
            ) : (
              <>
                {availableCourseSessions.length > 0 ? (
                  <>
                    {availableCourseSessions?.map((item: any, i: any) => (
                      <tr key={i}>
                        <td className="border-b border-[#c6c6c6] py-2 px-4 text-center w-1/5">
                          {item?.session_name}
                        </td>
                        <td className="border-b border-[#c6c6c6] py-2 px-4 text-center w-1/5">
                          {item?.session_language}
                        </td>
                        <td className="border-b border-[#c6c6c6] py-2 px-4 text-center w-1/5">
                          {item?.type === "ONLINE"
                            ? t("N/A")
                            : item?.location &&
                              item?.location !== "UNDEFINED" &&
                              item?.type !== "NOT_SET"
                            ? item?.location
                            : "MULTIPLE LOCATIONS"}
                        </td>
                        <td className="border-b border-[#c6c6c6] py-2 px-4 text-center w-1/5">
                          {item?.type === "ONSITE"
                            ? t("Physical attendance")
                            : t("Online")}
                        </td>
                        <td className="border-b border-[#c6c6c6] py-2 px-4 text-center w-1/5">
                          {item.events ? (
                            <span
                              className="text-blue-500 cursor-pointer underline"
                              onClick={() => {
                                {
                                  setCurrentSessionName(item.session_name);
                                  handleOpenModal(item.events);
                                }
                              }}
                            >
                              {t("Open")}
                            </span>
                          ) : (
                            t("N/A")
                          )}
                        </td>
                        <td className="border-b border-[#c6c6c6] py-2 px-4 text-center w-1/5">
                          {item.instructors
                            ? item.instructors
                                .split(",")
                                .map(
                                  (
                                    instructor: any,
                                    index: number,
                                    array: any
                                  ) => (
                                    <span key={index}>
                                      {instructor.trim()}
                                      {index !== array.length - 1 && ","} <br />
                                    </span>
                                  )
                                )
                            : t("N/A")}
                        </td>
                        {/* <td className="border-b border-[#c6c6c6] py-2 px-4 text-center w-1/5">
                          {item.session_status}
                        </td> */}
                      </tr>
                    ))}
                  </>
                ) : (
                  <tr className="pt-5">
                    <td colSpan={5} className="text-center">
                      {t("No Session Data found for")}{" "}
                      {selectedCourse.course_name}
                    </td>
                  </tr>
                )}
              </>
            )}
          </tbody>
          <EventDetailsModal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            modalData={modalData}
            modalName="Session Details"
            sessionName={currentSessionName}
          />
        </table>
      </div>
      {isSaveModalOpen && (
        <SaveConfirmModal
          closeModal={() => setIsSaveModalOpen(false)}
          handleSave={handleApply}
          loading={updateLoading}
          msg="Are you sure you want to apply for this course?"
        />
      )}
    </div>
  );
}

export default CourseSessions;
